import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { queryClient } from '../../../app/query-client';
import { queryKeys } from '../../../api/query-keys';
import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { cancelDocumentUpload, removeDocument, retryDocumentReconciliation, reviewDocument, type DocumentRemovalReason } from '../api/documents.api';
import { useDocument, useImageEvidence } from '../api/useDocuments';
import { DocumentStatusBadge } from '../components/DocumentStatusBadge';
import { documentReconciliationCompletedGuidance, documentRemovalSubmittedGuidance } from '../removal-guidance';
import { formatFileSize, isImageFileType, type DocumentReviewConflictDto, type DocumentReviewDto } from '../types/document';

type PendingAction = 'confirm-review' | 'reject-review' | 'remove-upload' | 'retry-upload' | 'remove-document' | 'retry-reconciliation' | null;

export function DocumentDetailPage() {
  const { machineId = '', documentId = '' } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const document = useDocument(documentId, auth.can(permissions.documentsRead));
  const imageEvidence = useImageEvidence(
    documentId,
    Boolean(
      auth.can(permissions.documentsRead)
      && document.data
      && isImageFileType(document.data.fileType)
      && document.data.imageEvidenceStatus === 'COMPLETED'
    ),
  );
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ title: string; body: string } | null>(null);
  const [removalReason, setRemovalReason] = useState<DocumentRemovalReason>('WRONG_DOCUMENT');
  const [rejected, setRejected] = useState(false);
  const previousReconciliationStatus = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const currentStatus = document.data?.removalReconciliationStatus ?? null;
    if (previousReconciliationStatus.current === 'PENDING' && currentStatus === 'COMPLETED') {
      setNotice(documentReconciliationCompletedGuidance());
    }
    previousReconciliationStatus.current = currentStatus;
  }, [document.data?.removalReconciliationStatus]);

  if (!auth.can(permissions.documentsRead)) return <Navigate to={`/machines/${machineId}`} replace />;

  if (document.isLoading) return <section className="r42-page r42-form-page"><div className="r42-inline-state">Loading document…</div></section>;
  if (document.isError || !document.data) {
    return (
      <section className="r42-page r42-form-page">
        <div className="r42-inline-state r42-inline-state--error">
          <span>Route42 couldn't load this document.</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => void document.refetch()}>Try again</button>
        </div>
      </section>
    );
  }

  const item = document.data;
  if (item.machineId !== machineId) return <Navigate to={`/machines/${machineId}/documents`} replace />;
  const removed = item.lifecycleStatus === 'REMOVED';
  const documentsBackTarget = removed ? `/machines/${machineId}/documents?view=removed` : `/machines/${machineId}/documents`;

  async function refreshDocument() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.document(auth.activeCustomerId, documentId) }),
      queryClient.invalidateQueries({ queryKey: queryKeys.documents(auth.activeCustomerId, machineId) }),
    ]);
    await document.refetch();
  }

  async function executePendingAction() {
    if (!pendingAction) return;
    setSubmitting(true);
    setActionError(null);
    try {
      if (pendingAction === 'confirm-review') {
        await reviewDocument(documentId, 'CONFIRM');
        setNotice({ title: 'Review complete.', body: 'Route42 resumed learning from this document.' });
        await refreshDocument();
      } else if (pendingAction === 'reject-review') {
        await reviewDocument(documentId, 'REJECT');
        await queryClient.invalidateQueries({ queryKey: queryKeys.documents(auth.activeCustomerId, machineId) });
        setRejected(true);
      } else if (pendingAction === 'remove-upload') {
        await cancelDocumentUpload(documentId, 'USER_REMOVED');
        await queryClient.invalidateQueries({ queryKey: queryKeys.documents(auth.activeCustomerId, machineId) });
        navigate(`/machines/${machineId}/documents`);
      } else if (pendingAction === 'retry-upload') {
        await cancelDocumentUpload(documentId, 'USER_RETRY');
        await queryClient.invalidateQueries({ queryKey: queryKeys.documents(auth.activeCustomerId, machineId) });
        navigate(`/machines/${machineId}/documents/add`);
      } else if (pendingAction === 'remove-document') {
        const removedDocument = await removeDocument(documentId, removalReason, item.version);
        if (removedDocument.machineEvidenceReconciliationStatus === 'ATTENTION_REQUIRED') {
          setNotice({
            title: 'Document removed',
            body: 'Route42 stopped using this source as evidence immediately. The Machine Memory update could not start safely, so it is paused for recovery instead of using stale knowledge.',
          });
        } else {
          setNotice(documentRemovalSubmittedGuidance());
        }
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: queryKeys.documents(auth.activeCustomerId, machineId) }),
          queryClient.invalidateQueries({ queryKey: queryKeys.machineMemory(auth.activeCustomerId, machineId) }),
        ]);
        await refreshDocument();
      } else if (pendingAction === 'retry-reconciliation') {
        if (!item.machineEvidenceRevision) throw new Error('Refresh this source before retrying the Machine Memory update.');
        const retried = await retryDocumentReconciliation(documentId, item.machineEvidenceRevision);
        if (retried.machineEvidenceReconciliationStatus === 'ATTENTION_REQUIRED') {
          setActionError('Route42 kept this source excluded, but the Machine Memory update could not restart. Try again, or contact your Route42 administrator if the problem continues.');
        } else {
          setNotice({
            title: 'Retry started',
            body: 'Route42 is retrying the Machine Memory update. This removed source stays excluded from new answers while recovery runs.',
          });
        }
        await refreshDocument();
      }
      setPendingAction(null);
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Route42 could not complete this document action.');
      setPendingAction(null);
    } finally {
      setSubmitting(false);
    }
  }

  if (rejected) {
    return (
      <section className="r42-page r42-form-page r42-document-detail-page">
        <Link className="r42-back-link" to={documentsBackTarget}><i className="bi bi-arrow-left" aria-hidden="true" /> {removed ? 'Removed documents' : 'Documents'}</Link>
        <div className="r42-document-resolution-card" role="status">
          <span className="r42-document-resolution-card__icon"><i className="bi bi-check-circle" aria-hidden="true" /></span>
          <div>
            <span className="r42-eyebrow">Review complete</span>
            <h1>Document rejected</h1>
            <p>Route42 will not use this document as machine knowledge. The human review decision remains in the audit trail.</p>
            <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents`}>Return to documents</Link>
          </div>
        </div>
      </section>
    );
  }

  const reviewRequired = item.uiStatus === 'needs-review' && item.review?.status === 'REQUIRED';
  const canReview = auth.can(permissions.documentsReview);
  const canConfirmReview = canReview && Boolean(item.review?.available_decisions.includes('CONFIRM'));
  const canRejectReview = canReview && Boolean(item.review?.available_decisions.includes('REJECT'));
  const interrupted = item.uiStatus === 'upload-interrupted';
  const machineReconciliationStatus = String(item.machineEvidenceReconciliationStatus || 'PENDING').toUpperCase();
  const reconciliationUnfinished = removed && item.removalReconciliationStatus === 'PENDING';
  const reconciliationRetrying = reconciliationUnfinished && machineReconciliationStatus === 'RETRY_PENDING';
  const reconciliationNeedsAttention = reconciliationUnfinished && machineReconciliationStatus === 'ATTENTION_REQUIRED';
  const reconciliationPending = reconciliationUnfinished && !reconciliationNeedsAttention;
  const noEvidence = item.uiStatus === 'no-evidence';
  const isImage = isImageFileType(item.fileType);
  const imageProcessingFailure = isImage && item.uiStatus === 'failed' && (item.imageEvidenceStatus === 'REVIEW_REQUIRED' || item.imageKnowledgeStatus === 'FAILED');
  const canRemoveDocument = auth.can(permissions.documentsRemove) && item.lifecycleStatus === 'ACTIVE' && item.uploadStatus !== 'AWAITING_UPLOAD' && !reviewRequired;

  return (
    <section className="r42-page r42-form-page r42-document-detail-page">
      <Link className="r42-back-link" to={documentsBackTarget}><i className="bi bi-arrow-left" aria-hidden="true" /> {removed ? 'Removed documents' : 'Documents'}</Link>
      <div className="r42-document-detail-head">
        <span className="r42-document-detail-icon"><i className={isImage ? 'bi bi-file-earmark-image' : 'bi bi-file-earmark-text'} aria-hidden="true" /></span>
        <div><span className="r42-eyebrow">{isImage ? 'Image evidence' : 'Document'}</span><h1>{item.filename}</h1></div>
        <DocumentStatusBadge status={item.uiStatus} />
      </div>

      {notice ? <div className="r42-form-alert r42-form-alert--success" role="status"><i className="bi bi-check-circle" aria-hidden="true" /><div><strong>{notice.title}</strong><span>{notice.body}</span></div></div> : null}
      {actionError ? <div className="r42-form-alert" role="alert"><i className="bi bi-exclamation-circle" aria-hidden="true" /><div><strong>Route42 needs your attention.</strong><span>{actionError}</span></div></div> : null}

      {removed ? (
        <section className={`r42-form-section r42-document-removal-status-card${reconciliationNeedsAttention ? ' r42-document-removal-status-card--attention' : ''}`} role={reconciliationNeedsAttention ? 'alert' : 'status'}>
          <span className="r42-eyebrow">Source removed</span>
          <h2>{reconciliationNeedsAttention
            ? 'Machine Memory update needs attention'
            : reconciliationRetrying
              ? 'Route42 is retrying the Machine Memory update'
              : reconciliationPending
                ? 'Route42 is updating Machine Memory'
                : 'This source is no longer used as evidence'}</h2>
          <p>{reconciliationNeedsAttention
            ? 'Route42 stopped after bounded retries instead of treating stale Machine Memory as current. The removed source remains excluded from every new answer.'
            : reconciliationRetrying
              ? 'A temporary problem interrupted the update. Route42 is retrying automatically while keeping the removed source excluded from active evidence.'
              : reconciliationPending
                ? 'This document was removed from active evidence immediately. Route42 is rebuilding shared machine understanding from the sources that remain active.'
                : 'Route42 completed the Machine Memory update. This document remains here only so your team can inspect the historical source and understand the removal decision.'}</p>
          <div className="r42-document-safety-note"><i className="bi bi-shield-check" aria-hidden="true" /><span>{reconciliationUnfinished ? 'Stale Machine Memory remains fenced. New reasoning can use only evidence that is still active.' : 'This source cannot contribute to new questions, investigations, or Machine Memory.'}</span></div>
          {reconciliationNeedsAttention && auth.can(permissions.documentsRemove) ? (
            <div className="r42-document-recovery-actions">
              <button className="btn r42-btn-primary" type="button" onClick={() => setPendingAction('retry-reconciliation')}>Retry Machine Memory update</button>
              <span>Retrying does not restore this document or make it active evidence again.</span>
            </div>
          ) : reconciliationNeedsAttention ? (
            <p className="r42-document-permission-note">Your current Route42 role can view this protected state but cannot retry the Machine Memory update.</p>
          ) : null}
        </section>
      ) : reviewRequired && item.review ? (
        <HumanReviewCard
          review={item.review}
          canConfirm={canConfirmReview}
          canReject={canRejectReview}
          onConfirm={() => setPendingAction('confirm-review')}
          onReject={() => setPendingAction('reject-review')}
        />
      ) : interrupted ? (
        <section className="r42-form-section r42-document-recovery-card">
          <span className="r42-eyebrow">Upload recovery</span>
          <h2>Route42 never received this file</h2>
          <p>The secure browser upload did not finish. No machine knowledge was created from this file, so it is safe to select the source again.</p>
          <div className="r42-document-safety-note"><i className="bi bi-shield-check" aria-hidden="true" /><span>Nothing from this interrupted upload was added to machine knowledge.</span></div>
          {auth.can(permissions.documentsUpload) ? (
            <div className="r42-document-review-actions">
              <button className="btn r42-btn-primary" type="button" onClick={() => setPendingAction('retry-upload')}>Upload again</button>
              <button className="btn btn-outline-secondary" type="button" onClick={() => setPendingAction('remove-upload')}>Remove</button>
            </div>
          ) : <p className="r42-document-permission-note">Your current Route42 role can view this interrupted upload but cannot remove or restart it.</p>}
        </section>
      ) : imageProcessingFailure ? (
        <section className="r42-form-section r42-document-recovery-card">
          <span className="r42-eyebrow">Image processing needs attention</span>
          <h2>{item.stageLabel}</h2>
          <p>Route42 could not safely complete this image-processing step after bounded retries. The original image remains protected, and incomplete or uncertain output was not added to Machine Memory.</p>
          <div className="r42-document-safety-note"><i className="bi bi-shield-check" aria-hidden="true" /><span>Route42 stopped rather than repeatedly spending on a failing analysis or treating partial output as evidence.</span></div>
          {auth.can(permissions.documentsUpload) ? <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add`}>Add another image</Link> : null}
        </section>
      ) : noEvidence ? (
        <section className="r42-form-section r42-image-no-evidence-card">
          <span className="r42-eyebrow">Image analysis complete</span>
          <h2>No clear machine evidence was added</h2>
          <p>Route42 received and analyzed this image, but it did not find visible machine evidence it could safely turn into a grounded observation. This is not a processing failure.</p>
          <div className="r42-image-no-evidence-next">
            <strong>If you expected Route42 to see something specific</strong>
            <span>Try a closer, clearer, or better-lit image that keeps the relevant component, HMI message, reading, nameplate, or label visible.</span>
          </div>
          <div className="r42-document-safety-note"><i className="bi bi-shield-check" aria-hidden="true" /><span>Route42 did not invent a diagnosis or add uncertain image content to Machine Memory.</span></div>
          {auth.can(permissions.documentsUpload) ? <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add`}>Add another image</Link> : null}
        </section>
      ) : (
        <section className="r42-form-section r42-document-learning-card">
          <span className="r42-eyebrow">Route42 learning</span>
          <h2>{item.stageLabel}</h2>
          <p>{learningDescription(item.uiStatus, item.questionReady, isImage)}</p>
          {(item.uiStatus === 'learning' || item.uiStatus === 'uploading') ? <div className="r42-learning-track"><span /></div> : null}
        </section>
      )}

      {isImage && item.imageEvidenceStatus === 'COMPLETED' ? (
        <ImageEvidencePanel
          evidence={imageEvidence.data ?? null}
          loading={imageEvidence.isLoading}
          error={imageEvidence.isError}
          onRetry={() => void imageEvidence.refetch()}
          filename={item.filename}
        />
      ) : null}

      <section className="r42-form-section">
        <div className="r42-form-section__head"><h2>{isImage ? 'Image details' : 'Document details'}</h2><p>{removed ? 'The historical source remains inspectable for provenance, but Route42 no longer treats it as active machine evidence.' : isImage ? 'The original image remains protected while Route42 turns supported visible observations into machine knowledge.' : 'The original source remains protected while Route42 builds machine knowledge from it.'}</p></div>
        <dl className="r42-document-facts">
          <Fact label="File type" value={item.fileType} />
          <Fact label="Size" value={formatFileSize(item.fileSizeBytes)} />
          <Fact label="Added" value={formatDate(item.uploadedAt ?? item.createdAt)} />
          {isImage && item.imageEvidenceGeneratedAt ? <Fact label="Analyzed" value={formatDate(item.imageEvidenceGeneratedAt)} /> : null}
          <Fact label="Knowledge ready" value={removed ? 'No — removed from evidence' : item.questionReady ? 'Yes' : 'Not yet'} />
          {removed ? <Fact label="Removed" value={formatDate(item.removedAt)} /> : null}
          {removed ? <Fact label="Removal reason" value={removalReasonLabel(item.removedReason)} /> : null}
          {removed ? <Fact label="Memory update" value={reconciliationPending ? 'In progress' : 'Complete'} /> : null}
          {item.duplicateStatus ? <Fact label="Duplicate check" value={humanize(item.duplicateStatus)} /> : null}
        </dl>
      </section>

      {canRemoveDocument ? (
        <section className="r42-document-removal-zone">
          <div>
            <strong>Wrong, duplicate, or outdated source?</strong>
            <span>Remove it from active evidence. Route42 will stop using it immediately and reconcile Machine Memory from the sources that remain.</span>
          </div>
          <button className="btn btn-sm btn-outline-danger" type="button" onClick={() => setPendingAction('remove-document')}>Remove document</button>
        </section>
      ) : null}

      {!removed && !reviewRequired && !interrupted && !noEvidence && !imageProcessingFailure ? (
        <div className="r42-document-detail-actions">
          {auth.can(permissions.documentsUpload) ? <Link className="btn btn-outline-secondary" to={`/machines/${machineId}/documents/add`}>Add more information</Link> : null}
          {item.questionReady && auth.can(permissions.conversationCreate) && auth.can(permissions.questionAsk) ? (
            <Link className="btn r42-btn-primary" to={`/machines/${machineId}/conversations/new?document=${encodeURIComponent(item.id)}`}>{isImage ? 'Ask about this image' : 'Ask about this document'}</Link>
          ) : (
            <button className="btn r42-btn-primary" disabled title={item.questionReady ? 'Your Route42 role does not allow questions.' : `Route42 is still learning from this ${isImage ? 'image' : 'document'}.`}>{isImage ? 'Ask about this image' : 'Ask about this document'}</button>
          )}
        </div>
      ) : null}

      {removed ? (
        <div className="r42-document-detail-actions">
          <Link className="btn btn-outline-secondary" to={`/machines/${machineId}/documents?view=removed`}>View removed sources</Link>
          {auth.can(permissions.documentsUpload) ? <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add`}>Add replacement information</Link> : null}
        </div>
      ) : null}

      {pendingAction ? (
        <DocumentActionDialog
          action={pendingAction}
          filename={item.filename}
          machineName={item.review?.selected_machine_name ?? null}
          submitting={submitting}
          removalReason={removalReason}
          onRemovalReasonChange={setRemovalReason}
          onCancel={() => setPendingAction(null)}
          onConfirm={() => void executePendingAction()}
        />
      ) : null}
    </section>
  );
}

function ImageEvidencePanel({
  evidence,
  loading,
  error,
  onRetry,
  filename,
}: {
  evidence: import('../types/document').ImageEvidenceDetailDto | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  filename: string;
}) {
  if (loading) {
    return (
      <section className="r42-form-section r42-image-evidence-panel" aria-label="Loading image evidence">
        <div className="r42-form-section__head">
          <h2>Visible evidence</h2>
          <p>Loading the bounded evidence Route42 recorded from this image…</p>
        </div>
        <div className="r42-image-evidence-loading"><span /><span /><span /></div>
      </section>
    );
  }

  if (error || !evidence) {
    return (
      <section className="r42-form-section r42-image-evidence-panel">
        <div className="r42-form-section__head">
          <h2>Visible evidence</h2>
          <p>Route42 analyzed this image, but the evidence details could not be loaded right now.</p>
        </div>
        <button type="button" className="btn btn-outline-secondary" onClick={onRetry}>Try again</button>
      </section>
    );
  }

  const hasObservations = evidence.observations.length > 0;
  const hasText = evidence.extracted_text.length > 0;
  const hasLimitations = evidence.limitations.length > 0;

  return (
    <section className="r42-form-section r42-image-evidence-panel">
      <div className="r42-form-section__head">
        <span className="r42-eyebrow">Evidence trail</span>
        <h2>What Route42 observed</h2>
        <p>{evidence.summary}</p>
      </div>

      <div className="r42-image-evidence-boundary">
        <i className="bi bi-eye" aria-hidden="true" />
        <div>
          <strong>Visible evidence, not a diagnosis</strong>
          <span>{evidence.guidance}</span>
        </div>
      </div>

      {hasObservations ? (
        <div className="r42-image-observation-list" aria-label="Visible observations">
          {evidence.observations.map((observation, index) => (
            <article className="r42-image-observation" key={`${observation.observation_type}-${index}`}>
              <div className="r42-image-observation__head">
                <span>{observationLabel(observation.observation_type)}</span>
                <EvidenceSupportBadge level={observation.support_level} />
              </div>
              <p>{observation.text}</p>
            </article>
          ))}
        </div>
      ) : (
        <div className="r42-image-evidence-empty">
          <strong>No bounded observations were added</strong>
          <span>Route42 did not find visible machine evidence it could safely record from {filename}.</span>
        </div>
      )}

      {hasText ? (
        <div className="r42-image-evidence-subsection">
          <div className="r42-image-evidence-subsection__head">
            <h3>Visible text</h3>
            <span>{evidence.text_extraction.used ? 'OCR enrichment used when helpful' : 'Read from the image analysis'}</span>
          </div>
          <div className="r42-image-text-list">
            {evidence.extracted_text.map((item, index) => (
              <div key={`${item.text}-${index}`}>
                <span className="r42-image-text-value">{item.text}</span>
                <span className="r42-image-text-context">{item.context || 'Visible image text'}</span>
                <EvidenceSupportBadge level={item.support_level} compact />
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {hasLimitations ? (
        <div className="r42-image-evidence-subsection">
          <div className="r42-image-evidence-subsection__head">
            <h3>What this image cannot establish</h3>
            <span>These limits travel with the evidence into Route42 reasoning.</span>
          </div>
          <ul className="r42-image-limitations">
            {evidence.limitations.map((limitation, index) => <li key={`${limitation}-${index}`}>{limitation}</li>)}
          </ul>
        </div>
      ) : null}

      <div className="r42-image-evidence-lineage">
        <div>
          <span className="r42-image-lineage-icon"><i className="bi bi-image" aria-hidden="true" /></span>
          <span><strong>Original image</strong><small>{filename}</small></span>
        </div>
        <i className="bi bi-chevron-right" aria-hidden="true" />
        <div>
          <span className="r42-image-lineage-icon"><i className="bi bi-eye" aria-hidden="true" /></span>
          <span><strong>Visible evidence analyzed</strong><small>{formatDate(evidence.analyzed_at)}</small></span>
        </div>
        <i className="bi bi-chevron-right" aria-hidden="true" />
        <div>
          <span className="r42-image-lineage-icon"><i className="bi bi-database-check" aria-hidden="true" /></span>
          <span>
            <strong>{knowledgeLineageTitle(evidence)}</strong>
            <small>{knowledgeLineageLabel(evidence)}</small>
          </span>
        </div>
      </div>

      <p className="r42-image-support-note">
        <i className="bi bi-info-circle" aria-hidden="true" />
        <span>Visual support describes how clearly the image supports an observation. It is not a probability that a diagnosis is correct.</span>
      </p>
    </section>
  );
}

function EvidenceSupportBadge({ level, compact = false }: {
  level: import('../types/document').ImageEvidenceSupportLevel;
  compact?: boolean;
}) {
  const label = level === 'STRONG' ? 'Strong visual support'
    : level === 'MODERATE' ? 'Moderate visual support'
      : level === 'LIMITED' ? 'Limited visual support'
        : 'Support not rated';
  return <span className={`r42-image-support-badge r42-image-support-badge--${level.toLocaleLowerCase()}${compact ? ' is-compact' : ''}`}>{label}</span>;
}

function observationLabel(value: string): string {
  const labels: Record<string, string> = {
    VISIBLE_CONDITION: 'Visible condition',
    VISIBLE_DAMAGE_OR_WEAR: 'Visible damage or wear',
    VISIBLE_RESIDUE_OR_DISCOLORATION: 'Visible residue or discoloration',
    VISIBLE_READING: 'Visible reading',
    VISIBLE_STATUS_OR_INDICATOR: 'Visible status or indicator',
    VISIBLE_IDENTIFIER: 'Visible identifier',
    VISIBLE_TEXT: 'Visible text',
    OTHER_VISIBLE_EVIDENCE: 'Other visible evidence',
  };
  return labels[value] ?? humanize(value);
}

function knowledgeLineageTitle(evidence: import('../types/document').ImageEvidenceDetailDto): string {
  if (evidence.knowledge.status === 'NO_ACTIONABLE_EVIDENCE') return 'No uncertain knowledge added';
  if (evidence.knowledge.status === 'COMPLETED' || evidence.knowledge.fact_count > 0 || evidence.knowledge.chunk_count > 0) return 'Grounded knowledge created';
  return 'Knowledge processing';
}

function knowledgeLineageLabel(evidence: import('../types/document').ImageEvidenceDetailDto): string {
  if (evidence.knowledge.status === 'NO_ACTIONABLE_EVIDENCE') return 'Route42 preserved the analysis without manufacturing machine knowledge.';
  const parts = [];
  if (evidence.knowledge.fact_count) parts.push(`${evidence.knowledge.fact_count} ${evidence.knowledge.fact_count === 1 ? 'fact' : 'facts'}`);
  if (evidence.knowledge.chunk_count) parts.push(`${evidence.knowledge.chunk_count} ${evidence.knowledge.chunk_count === 1 ? 'knowledge chunk' : 'knowledge chunks'}`);
  return parts.length ? parts.join(' · ') : 'Evidence is moving through the machine knowledge pipeline.';
}

function HumanReviewCard({
  review,
  canConfirm,
  canReject,
  onConfirm,
  onReject,
}: {
  review: DocumentReviewDto;
  canConfirm: boolean;
  canReject: boolean;
  onConfirm: () => void;
  onReject: () => void;
}) {
  const machineName = review.selected_machine_name || 'the selected machine';
  const machineAssociation = review.type === 'MACHINE_ASSOCIATION';
  return (
    <section className="r42-form-section r42-document-review-card">
      <span className="r42-eyebrow">Human review</span>
      <h2>{machineAssociation ? `Confirm this document belongs to ${machineName}` : 'Review this document before Route42 continues'}</h2>
      <p>{review.summary}</p>

      {machineAssociation ? (
        <div className="r42-document-review-summary">
          <div><span>Route42 assessment</span><strong>{humanize(review.assessment || 'Possible mismatch')}</strong></div>
          {typeof review.match_confidence === 'number' ? <div><span>Association confidence</span><strong>{Math.round(review.match_confidence * 100)}%</strong></div> : null}
        </div>
      ) : null}

      {review.conflicts.length ? (
        <div className="r42-document-review-conflicts">
          <h3>Why Route42 stopped</h3>
          <p>At least one strong machine identifier in the document conflicts with the machine you selected.</p>
          {review.conflicts.map((conflict, index) => <ReviewConflict key={`${conflict.field}-${index}`} conflict={conflict} />)}
        </div>
      ) : (
        <div className="r42-document-review-conflicts">
          <h3>Why Route42 stopped</h3>
          <p>{review.summary}</p>
          {review.legacy_review_context ? <p className="r42-document-permission-note">This review was created before Route42 retained the detailed identifier comparison shown on newer reviews. Confirm only after checking the source really belongs to this machine.</p> : null}
        </div>
      )}

      {review.multiple_machines_detected ? <div className="r42-document-review-warning"><i className="bi bi-exclamation-triangle" aria-hidden="true" /><span>This document may reference more than one machine. Review the identifiers carefully before continuing.</span></div> : null}
      <div className="r42-document-safety-note"><i className="bi bi-shield-check" aria-hidden="true" /><span>Route42 stopped before using this document as machine knowledge. Nothing from it has been silently accepted as evidence.</span></div>

      {canConfirm || canReject ? (
        <div className="r42-document-review-actions">
          {canConfirm ? <button type="button" className="btn r42-btn-primary" onClick={onConfirm}>Confirm and continue</button> : null}
          {canReject ? <button type="button" className="btn btn-outline-secondary" onClick={onReject}>Reject document</button> : null}
        </div>
      ) : <p className="r42-document-permission-note">Your current Route42 role can view this review but cannot make the review decision.</p>}
    </section>
  );
}

function ReviewConflict({ conflict }: { conflict: DocumentReviewConflictDto }) {
  return (
    <div className="r42-document-conflict-row">
      <strong>{reviewFieldLabel(conflict.field)}</strong>
      <div><span>Selected machine</span><b>{conflict.selected_value || 'Not specified'}</b></div>
      <div><span>Document</span><b>{conflict.document_value || 'Not specified'}</b></div>
      {conflict.source_reference?.page ? <small>Found on page {conflict.source_reference.page}</small> : null}
    </div>
  );
}

function DocumentActionDialog({ action, filename, machineName, submitting, removalReason, onRemovalReasonChange, onCancel, onConfirm }: {
  action: Exclude<PendingAction, null>;
  filename: string;
  machineName: string | null;
  submitting: boolean;
  removalReason: DocumentRemovalReason;
  onRemovalReasonChange: (reason: DocumentRemovalReason) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const content = actionDialogContent(action, filename, machineName);
  return (
    <div className="r42-dialog-layer" role="dialog" aria-modal="true" aria-labelledby="document-action-title">
      <button className="r42-dialog-backdrop" type="button" aria-label="Close document action" onClick={onCancel} />
      <div className="r42-dialog-card r42-document-action-dialog">
        <span className="r42-eyebrow">{content.eyebrow}</span>
        <h2 id="document-action-title">{content.title}</h2>
        <p>{content.body}</p>
        {action === 'remove-document' ? (
          <label className="r42-document-removal-reason">
            <span>Why are you removing it?</span>
            <select value={removalReason} onChange={(event) => onRemovalReasonChange(event.target.value as DocumentRemovalReason)} disabled={submitting}>
              <option value="WRONG_DOCUMENT">Wrong document</option>
              <option value="DUPLICATE_DOCUMENT">Duplicate source</option>
              <option value="OUTDATED_DOCUMENT">Outdated source</option>
              <option value="OTHER">Other reason</option>
            </select>
          </label>
        ) : null}
        <div className="r42-dialog-actions">
          <button className="btn btn-outline-secondary" type="button" onClick={onCancel} disabled={submitting}>{content.cancelLabel}</button>
          <button className={`btn ${content.destructive ? 'btn-outline-danger' : 'r42-btn-primary'}`} type="button" onClick={onConfirm} disabled={submitting}>{submitting ? 'Working…' : content.confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

function actionDialogContent(action: Exclude<PendingAction, null>, filename: string, machineName: string | null) {
  if (action === 'confirm-review') return {
    eyebrow: 'Human review',
    title: 'Confirm this document?',
    body: `I reviewed ${filename} and confirm that it belongs to ${machineName || 'the selected machine'}. Route42 will record this decision and resume learning from the document.`,
    cancelLabel: 'Review again',
    confirmLabel: 'Confirm and continue',
    destructive: false,
  };
  if (action === 'reject-review') return {
    eyebrow: 'Human review',
    title: 'Reject this document?',
    body: 'Route42 will not use this document as machine knowledge. The review decision will remain in the audit trail.',
    cancelLabel: 'Keep reviewing',
    confirmLabel: 'Reject document',
    destructive: true,
  };
  if (action === 'remove-document') return {
    eyebrow: 'Machine evidence',
    title: `Remove ${filename} from Machine Memory?`,
    body: 'Route42 will stop using this source as evidence immediately. Machine Memory will then be reconciled from the remaining active evidence. The historical source and audit trail stay inspectable.',
    cancelLabel: 'Keep document',
    confirmLabel: 'Remove document',
    destructive: true,
  };
  if (action === 'retry-reconciliation') return {
    eyebrow: 'Recovery',
    title: 'Retry the Machine Memory update?',
    body: 'Route42 will retry the current evidence revision using only sources that are still active. This removed document stays excluded throughout recovery.',
    cancelLabel: 'Not now',
    confirmLabel: 'Retry update',
    destructive: false,
  };
  if (action === 'retry-upload') return {
    eyebrow: 'Upload recovery',
    title: 'Upload this source again?',
    body: 'Route42 will remove the interrupted upload record and return you to Add documents. Your browser will ask you to select the source file again.',
    cancelLabel: 'Stay here',
    confirmLabel: 'Upload again',
    destructive: false,
  };
  return {
    eyebrow: 'Upload recovery',
    title: 'Remove this interrupted upload?',
    body: 'Route42 never received the file, so no machine knowledge will be removed. The abandoned upload record will be kept in the audit trail.',
    cancelLabel: 'Keep it',
    confirmLabel: 'Remove',
    destructive: true,
  };
}


function removalReasonLabel(value: string | null): string {
  if (!value) return 'Not specified';
  const labels: Record<string, string> = {
    WRONG_DOCUMENT: 'Wrong document',
    DUPLICATE_DOCUMENT: 'Duplicate source',
    OUTDATED_DOCUMENT: 'Outdated source',
    OTHER: 'Other reason',
  };
  return labels[value] ?? humanize(value);
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? '—' : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function humanize(value: string): string {
  return value.toLocaleLowerCase().replaceAll('_', ' ').replace(/^./, (char) => char.toLocaleUpperCase());
}

function reviewFieldLabel(value: string): string {
  const labels: Record<string, string> = {
    asset_id: 'Asset ID',
    serial_number: 'Serial number',
    model: 'Model',
    production_line: 'Production line',
    machine_name: 'Machine name',
    manufacturer: 'Manufacturer',
    location: 'Location',
  };
  return labels[value] ?? humanize(value);
}

function learningDescription(status: string, questionReady: boolean, isImage: boolean): string {
  if (status === 'failed') return `Route42 could not finish learning from this ${isImage ? 'image' : 'document'}. The source has not been silently treated as usable evidence.`;
  if (questionReady) return `This ${isImage ? 'image' : 'document'} has produced grounded machine knowledge that can support questions. Broader machine intelligence may continue updating in the background.`;
  if (isImage) return 'You can leave this page. Route42 is analyzing visible conditions and readable text, and will add only observations it can support. Image evidence alone is not treated as a diagnosis.';
  return 'You can leave this page. Route42 will continue processing the document in the background and this status will update automatically.';
}
